import { FC, useEffect, useRef } from "react";
import Layout from "@/components/layout";
import { useTranslation } from "react-i18next";
import styles from "./index.module.scss";
import Receipt from "./receipt";
import { Button } from "@/components/button";
import { useQuery } from "@tanstack/react-query";
import api from "@/services/api";
import LoadingScreen from "@/components/loading-screen";
import ProductRow from "./product-row";
import SearchProductModal from "./search-product-modal";
import { useNavigate } from "react-router-dom";
import ErrorScreen from "@/components/error-screen";
import AgeCheckModal from "./modals/AgeCheckModal";
import { Routes } from "@/types/routes";
import ProductNotFoundModal from "./product-not-found-modal";
import useModalsStore from "@/stores/modals";
import { StillThereModal } from "@/components/still-there-modal";
import isE2e from "@/helpers/isE2e";
import ErrorInfoModals from "@/components/error-info-modals";
import { logFirebaseEvent } from "@/services/firebase";
import { playSound, SoundOption } from "@/helpers/playSound";
import { useIsTakeOut } from "@/stores/cash-register";
import { useAppSelector } from "@/hooks/useAppSelector";
import { selectCartProducts, selectCartTotal } from "@/store/cart/selectors";
import { selectProducts } from "@/store/product/selectors";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { fetchProducts } from "@/store/product/async-thunk";
import { selectCombiDealModals } from "@/store/ui/selectors";
import CombiDealModal from "./modals/CombiDealModal";
import useScanning from "@/hooks/useScanning";

const MainPage: FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const cartProducts = useAppSelector(selectCartProducts);
  const cartTotal = useAppSelector(selectCartTotal);
  const isTakeOut = useIsTakeOut();
  const { isSearchModalOpen, toggleIsSearchModalOpen } = useModalsStore();
  const isSearchModalOpenRef = useRef<boolean>(isSearchModalOpen);
  isSearchModalOpenRef.current = isSearchModalOpen;
  const hasFetchedProductsRef = useRef(false);
  const { isLoading: isLoadingProducts, isError: isErrorProducts, products } = useAppSelector(selectProducts);
  const {
    isLoading: isLoadingCategories,
    error: errorCategories,
    refetch: fetchCategories,
  } = useQuery(["categories"], api.categories.getAll);
  const { invalidEanCode, setInvalidEanCode } = useModalsStore();
  const combiDealModals = useAppSelector(selectCombiDealModals);

  useScanning();

  const tryAgain = () => {
    dispatch(fetchProducts());
    fetchCategories();
  };

  // Products stay when reloading on main page, mainly for development purpose
  useEffect(() => {
    if (products.length === 0 && !isLoadingProducts && !hasFetchedProductsRef.current) {
      hasFetchedProductsRef.current = true;
      dispatch(fetchProducts());
    }
  }, [dispatch, products, isLoadingProducts]);


  if (isLoadingProducts || isLoadingCategories) return <LoadingScreen />;
  if (isErrorProducts || errorCategories) return <ErrorScreen onTryAgain={tryAgain} />;

  const proceed = () => {
    logFirebaseEvent("checkout_button_pressed", {
      total_items: cartProducts.length,
      total_price: cartTotal,
    });
    playSound(SoundOption.PaymentStart);
    if (isTakeOut) navigate(Routes.CHECKOUT);
    else navigate(Routes.PICK_UP_TIME_SLOT);
  };

  const onSearchPress = () => {
    playSound(SoundOption.Click1);
    toggleIsSearchModalOpen();
    logFirebaseEvent("search_card_pressed");
  };

  return (
    <Layout childrenClassName={styles.contentBox}>
      <h1 data-cy="main-page-title">
        {isTakeOut ? t("mainPage.pageTitle") : t("mainPage.pickupPageTitle")}
      </h1>
      <ProductRow onSearchPress={onSearchPress} />
      <div className={styles.receiptWrapper}>
        <Receipt />
        <Button
          className={styles.checkoutButton}
          text={isTakeOut ? t("mainPage.checkout") : t("ui.continue")}
          onClick={proceed}
          disabled={cartProducts.length === 0}
          data-cy="continue-button"
        />
      </div>
      <SearchProductModal
        title={t("mainPage.productSearch")}
        visible={isSearchModalOpen}
        onClose={toggleIsSearchModalOpen}
      />
      <AgeCheckModal />
      {invalidEanCode !== null && (
        <ProductNotFoundModal
          onClose={() => {
            setInvalidEanCode(null);
          }}
          invalidEanCode={invalidEanCode}
        />
      )}

      {combiDealModals.length > 0 && <CombiDealModal {...combiDealModals[0]} />}
      <ErrorInfoModals />
      {(import.meta.env.MODE === "production" || isE2e) && <StillThereModal />}
    </Layout>
  );
};

export default MainPage;
